import { useSelector } from 'react-redux';
import React, { useCallback, useEffect, useState } from 'react';
import SearchSeo from './searchSeo/SearchSeo';
import useSearchParams from './hooks/useSearchParams';
import Products from './products/Products';
import { useDispatch } from 'react-redux';
import { setBasicFilters } from '@/redux/store/actions/search';
import { BasicFiltersStateProps } from '@/redux/store/types';
import dynamic from 'next/dynamic';
import useSearchAnalytics from './hooks/useSearchAnalytics';
import LogoContainer from './logoContainer/LogoContainer';
import useDevice from '@/hooks/useDevice';
import { useRouter } from 'next/router';
import { RouteParams } from './types';
import MidtermDiscountPopup from './midtermDiscountPopup/MidtermDiscountPopup';
import { fetchSearch } from './_helpers/fetchData';
import { SearchDataProps } from './_types/searchDataType';
import ProductCardSkeleton from '@/components/reusable/productCard/productCardSkeleton/ProductCardSkeleton';
import { useIsWhiteLabel } from '../app/providers/whiteLabelProvider/WhiteLabelProvider';
import heap from '@/helpers/heap';
const Header = dynamic(() => import('../layout/header/Header'), {
  ssr: true
});
const NavigationBar = dynamic(() => import('../layout/NavigationBar/NavigationBar'), {
  ssr: true
});
const SelectCheckinPopUp = dynamic(() => import('./selectCheckinPopUp/SelectCheckinPopUp'), {
  ssr: true
});
const Filters = dynamic(() => import('./filters/Filters'), {
  ssr: true
});
const MidTermGoftino = dynamic(() => import('../reusable/midTermGoftino/MidtermGoftino'));
export interface SearchDataType {
  loading: boolean;
  data: SearchDataProps;
}
const SearchDefaultData: SearchDataProps = {
  list: null,
  map_list: null,
  midterm_map_list: null,
  loading: false,
  region_data: null,
  pagination: {
    total: 0,
    current_page: 0
  },
  search_id: ''
};
const perPage = 24;
const Search = ({
  firstData,
  isBot,
  host
}) => {
  const {
    params,
    queries,
    routerAsPath
  } = useSearchParams();
  const isWhiteLabel = useIsWhiteLabel();
  const router = useRouter();
  const {
    intrackSearchView
  } = useSearchAnalytics();
  const {
    isMobile
  } = useDevice();
  const [searchData, setSearchData] = useState<SearchDataType>({
    loading: true,
    data: firstData
  });
  const [isShowMap, setIsShowMap] = useState<boolean>(false);
  const [showMidtermGoftino, setShowMidtermGoftino] = useState(false);
  const [mapFullscreen, setMapFullscreen] = useState<boolean>(false);
  const {
    checkin,
    checkout,
    guests
  }: BasicFiltersStateProps = useSelector(state => state.basicFilters);
  const token = useSelector(state => state.token);
  const dispatch = useDispatch();
  const basicFiltersToLocalStorage = useCallback(() => {
    // clear checkin, checkout, guests from localstorage after 30 days
    dispatch(setBasicFilters());

    // set new checkin, checkout, guests filters into local storage
    if (queries.checkin && queries.checkout) {
      dispatch(setBasicFilters({
        checkin: +queries.checkin,
        checkout: +queries.checkout,
        guests
      }));
    }
    if (queries.guests) {
      dispatch(setBasicFilters({
        checkin: Number(queries.checkin) || checkin,
        checkout: Number(queries.checkout) || checkout,
        guests: +queries.guests
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkin, checkout, guests, queries.checkin, queries.checkout, queries.guests]);
  useEffect(() => {
    basicFiltersToLocalStorage();
    if (searchData?.data?.search_id) {
      intrackSearchView({
        roomsCount: searchData?.data?.pagination?.total,
        regionData: searchData?.data?.region_data
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerAsPath, searchData?.data?.search_id]);
  useEffect(() => {
    const handleFetchData = async () => {
      if (!isBot) {
        if (firstData?.list?.length > 0) {
          setSearchData({
            loading: true,
            data: SearchDefaultData
          });
          try {
            const result = await fetchSearch(router.query);
            setSearchData({
              loading: false,
              data: result
            });
          } catch (error) {
            setSearchData(prev => ({
              ...prev,
              loading: false
            }));
          }
        }
      } else {
        setSearchData({
          loading: false,
          data: firstData
        });
      }
    };
    handleFetchData();
    heap.track('search_view', {
      is_ads: !!searchData?.data?.list?.[0]?.badges?.includes('is_ad'),
      is_login: !!token
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routerAsPath]);
  return <>
      <SearchSeo perPage={perPage} queries={queries} params={params as RouteParams} data={searchData.data} host={host} data-sentry-element="SearchSeo" data-sentry-source-file="Search.tsx" />
      {!isMobile ? <Header defaultLightMode mapIsOpen={isShowMap} params={params} /> : !isWhiteLabel && <LogoContainer />}
      {(!router.query.host_uid || !isMobile) && <Filters isShowMap={isShowMap} setIsShowMap={setIsShowMap} setMapFullscreen={setMapFullscreen} />}
      <Products params={params as RouteParams} isShowMap={isShowMap} perPage={perPage} mapFullscreen={mapFullscreen} setMapFullscreen={setMapFullscreen} data={searchData.data} loading={searchData.loading} firstData={firstData} isBot={isBot} data-sentry-element="Products" data-sentry-source-file="Search.tsx" />
      {isMobile && <>
          {' '}
          <NavigationBar isShowMap={isShowMap} setIsShowMap={setIsShowMap} setShowMidtermGoftino={setShowMidtermGoftino} />
        </>}
      {showMidtermGoftino && <MidTermGoftino onClose={() => {
      setShowMidtermGoftino(false);
    }} />}
      {isMobile && !router.query.mid_term && <SelectCheckinPopUp />}
      <MidtermDiscountPopup data-sentry-element="MidtermDiscountPopup" data-sentry-source-file="Search.tsx" />
    </>;
};
export default Search;